import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { HeroSearch } from '@/components/home/HeroSearch';
import { PremiumBenefits } from '@/components/home/PremiumBenefits';
import { Testimonials } from '@/components/home/Testimonials';
import { ListingCard } from '@/components/common/ListingCard';
import { SkeletonGrid } from '@/components/common/SkeletonCard';
import { AdSlot } from '@/components/common/AdSlot';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { getFeaturedListings, getCities } from '@/services/api';

export default function Home() {
  const revealRef = useScrollReveal();
  const [featured, setFeatured] = useState(null);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    getFeaturedListings(8).then(setFeatured);
    getCities().then(setCities);

    const warmup = () => { import('@react-google-maps/api').catch(() => {}); };
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(warmup);
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(warmup, 1);
      return () => clearTimeout(id);
    }
  }, []);

  return (
    <div ref={revealRef}>
      {/* HERO */}
      <section data-testid="home-hero" className="relative hero-gradient noise-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-36 sm:pt-24 sm:pb-44">
          <div className="max-w-2xl">
            <div className="flex flex-wrap gap-2">
              {['Verified listings', 'Local agents', 'Real-time price drops'].map((chip) => (
                <span key={chip} className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                  <ShieldCheck size={13} /> {chip}
                </span>
              ))}
            </div>
            <h1 className="font-display mt-5 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Find a place you&rsquo;ll love to call home
            </h1>
            <p className="mt-4 max-w-xl text-base text-white/85 sm:text-lg">
              Search millions of homes for sale and rent, discover new construction, and connect with trusted agents &mdash; all in one modern platform.
            </p>
          </div>
        </div>
        {/* Floating search panel overlapping next section */}
        <div className="absolute inset-x-0 bottom-0 z-20 flex translate-y-1/2 justify-center px-3 sm:px-4">
          <HeroSearch />
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-40 pb-16">
        <div className="reveal flex items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Featured listings</h2>
            <p className="mt-1 text-slate-600">Hand-picked homes generating buzz right now.</p>
          </div>
          <Link to="/map" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:gap-2 transition-all">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="reveal mt-8">
          {!featured ? (
            <SkeletonGrid count={8} />
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {featured.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          )}
        </div>
      </section>

      {/* AD ZONE A */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="reveal"><AdSlot slotId="home-banner-a" size="banner" /></div>
      </section>

      {/* EXPLORE BY CITY */}
      <section className="bg-sky-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Explore by city</h2>
            <p className="mt-1 text-slate-600">Discover homes in the country&rsquo;s most in-demand markets.</p>
          </div>
          <div className="reveal mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
            {cities.map((c) => (
              <Link key={c.id} to={`/map?city=${encodeURIComponent(c.name)}`} className="group relative overflow-hidden rounded-2xl shadow-md" data-testid={`city-card-${c.id}`}>
                <img src={c.image} alt={c.name} className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-52" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-display text-lg font-semibold">{c.name}, {c.state}</p>
                  <p className="text-sm text-white/85">{c.count.toLocaleString('en-US')} listings</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PREMIUM BENEFITS */}
      <PremiumBenefits />

      <Testimonials />
    </div>
  );
}
